import groupBy from '../../../shared/helpers/groupBy';
import { OpzetCalendarItem, OpzetHolidayItem } from '../../../shared/types/opzet';
import { CalendarItem } from '../types';

export const fetchCalendarItems = async (bagId: string, baseQuery: (arg: string) => any): Promise<CalendarItem[]> => {
  const today = new Date();
  const thisYear = today.getFullYear();
  const nextYear = thisYear + 1;

  async function fetchEventsForYear(year: number) {
    return Promise.all([
      (await baseQuery(`/rest/adressen/${bagId}/kalender/${year}`) as { data: OpzetCalendarItem[] }).data,
      (await baseQuery(`/rest/feestdagen/${year}-01-01/${year}-12-31`) as { data: OpzetHolidayItem[] }).data,
    ]).then(([pickups, holidays]) => {
      return {
        pickups: filterPickupMomentsInYear(pickups, year),
        holidays: holidays || [],
      };
    });
  }

  // We fetch the pickup moments per year by giving the API a parameter `year`
  // that specifies for which year we want pickup moments. Though sometimes the
  // API doesn't respect that parameter and just blatantly returns pickup moments
  // for another year than we specified in the parameter. I know right..
  // That's why after the fact we filter the pickup moments on our side. Filtering
  // out any pickup moments that shouldn't be there.
  function filterPickupMomentsInYear(pickupMoments: OpzetCalendarItem[], year: number) {
    return groupBy(pickupMoments, (item) => String(new Date(item.ophaaldatum).getFullYear()))[year] || [];
  }

  function normalizeCalendarItem(item: (OpzetCalendarItem | OpzetHolidayItem)) {
    const isPickupMoment = 'afvalstroom_id' in item;

    // Pickup moment.
    if (isPickupMoment) {
      return {
        type: 'pickupMoment',
        date: item.ophaaldatum,
        wasteStreamId: item.afvalstroom_id,
      } as const;
    }

    // Holiday item.
    return {
      type: 'holiday',
      date: item.datum,
      holiday: item.naam,
    } as const;
  }

  const {
    pickups: pickupMomentsThisYear,
    holidays: holidaysThisYear,
  } = await fetchEventsForYear(thisYear);

  const {
    pickups: pickupMomentsNextYear,
    holidays: holidaysNextYear,
  } = await fetchEventsForYear(nextYear);

  const addressHasNoPickupMoments = !pickupMomentsThisYear.length && !pickupMomentsNextYear.length;

  if (addressHasNoPickupMoments) {
    return [];
  }

  return [...pickupMomentsThisYear, ...holidaysThisYear, ...pickupMomentsNextYear, ...holidaysNextYear].map(normalizeCalendarItem);
};
